import { RoomMemberEvent } from 'matrix-js-sdk'
import type { MatrixClient, RoomMember } from 'matrix-js-sdk'
import { startMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { formatMatrixDisplayName } from '@/modules/chat-matrix-fast/services/matrixUserId'
import { isMatrixHiddenUser } from '@/modules/chat-matrix-fast/constants/chat'

type TypingListener = (userNames: string[]) => void

function getMemberDisplayName(member: RoomMember): string {
  const raw = member.name || member.rawDisplayName || member.userId
  return raw.startsWith('@') ? formatMatrixDisplayName(raw) : raw
}

function getTypingUserNames(matrixClient: MatrixClient, roomId: string): string[] {
  const room = matrixClient.getRoom(roomId)
  const myUserId = matrixClient.getUserId()
  if (!room || !myUserId) {
    return []
  }

  return room.currentState
    .getMembers()
    .filter(member => member.typing && member.userId !== myUserId && !isMatrixHiddenUser(member.userId))
    .map(getMemberDisplayName)
}

export function subscribeMatrixTyping(roomId: string, onUpdate: TypingListener): () => void {
  let matrixClient: MatrixClient | undefined
  let stopped = false

  const publish = () => {
    if (stopped || !matrixClient) {
      return
    }

    onUpdate(getTypingUserNames(matrixClient, roomId))
  }

  const onTyping = (_event: unknown, member: RoomMember) => {
    if (member.roomId === roomId) {
      publish()
    }
  }

  void startMatrixClient().then(startedClient => {
    if (stopped) {
      return
    }

    matrixClient = startedClient
    matrixClient.on(RoomMemberEvent.Typing, onTyping)
    publish()
  })

  return () => {
    stopped = true
    matrixClient?.removeListener(RoomMemberEvent.Typing, onTyping)
  }
}

export async function sendMatrixTyping(roomId: string, isTyping: boolean, timeoutMs = 6000): Promise<void> {
  const matrixClient = await startMatrixClient()
  const room = matrixClient.getRoom(roomId)
  if (!room || room.getMyMembership() !== 'join') {
    // Без join Synapse отвечает 403 — не шумим в консоли.
    return
  }
  await matrixClient.sendTyping(roomId, isTyping, timeoutMs)
}
